import { Fragment, useState } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { BellIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';

interface Notification {
  id: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
}

const severityConfig = {
  INFO: { dot: 'bg-sky', text: 'text-sky', bg: 'bg-sky/10' },
  WARNING: { dot: 'bg-yellow-400', text: 'text-yellow-400', bg: 'bg-yellow-400/10' },
  CRITICAL: { dot: 'bg-alert', text: 'text-alert', bg: 'bg-alert/10' },
};

// TODO: Wire up to real alertsApi endpoint for live notifications

export default function NotificationDropdown() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  return (
    <Popover className="relative">
      <Popover.Button className="relative p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-navy-700 focus:outline-none">
        <BellIcon className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-alert text-[10px] font-bold text-white">
            {unreadCount}
          </span>
        )}
      </Popover.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <Popover.Panel className="absolute right-0 mt-2 w-96 rounded-xl bg-navy-800 border border-navy-700 shadow-2xl z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-navy-700">
            <h3 className="text-sm font-semibold text-white">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1 text-xs text-accent hover:text-accent-light transition-colors"
              >
                <CheckCircleIcon className="h-3.5 w-3.5" />
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications.map((notification) => {
              const config = severityConfig[notification.severity];
              return (
                <div
                  key={notification.id}
                  className={`px-4 py-3 border-b border-navy-700/50 hover:bg-navy-700/30 transition-colors ${
                    !notification.read ? 'bg-navy-700/20' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${config.dot} ${!notification.read ? 'animate-pulse-dot' : 'opacity-50'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium ${config.text}`}>
                          {notification.severity}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDistanceToNow(notification.timestamp, { addSuffix: true })}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-white mt-0.5 truncate">
                        {notification.title}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5 truncate">
                        {notification.message}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="px-4 py-3 border-t border-navy-700">
            <button className="w-full text-center text-xs text-accent hover:text-accent-light transition-colors font-medium">
              View all notifications
            </button>
          </div>
        </Popover.Panel>
      </Transition>
    </Popover>
  );
}
